import {
  CurrencyPipe,
  NumberSymbol,
  getLocaleNumberSymbol,
} from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[qtCurrencyControl]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CurrencyInputDirective,
      multi: true,
    },
    CurrencyPipe,
  ],
  standalone: true,
})
export class CurrencyInputDirective implements ControlValueAccessor {
  private _elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private _renderer = inject(Renderer2);
  private currencyPipe = inject(CurrencyPipe);

  @Input() currency = 'USD';
  @Input() display = 'symbol-narrow';
  @Input() digitsInfo = '1.0-2';

  private _value: string;
  private readonly decimalSymbol = getLocaleNumberSymbol(
    'en-US',
    NumberSymbol.Decimal
  );
  private readonly reDecimalSymbol = new RegExp(
    this.reQuote(this.decimalSymbol),
    'g'
  );
  private readonly reNonCurrencySymbols = new RegExp(
    `[^0-9${this.reQuote(this.decimalSymbol)}]`,
    'g'
  );

  @HostListener('input', ['$event'])
  onInput(event: any) {
    this._value = this.getValueFromEvent(event);
    this.onChange(this.removeCurrencyFormat(this._value));
    event.target.value = this._value;
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any) {
    this._value = this.getValueFromEvent(event);
    if (this._value?.length - this._value?.indexOf(this.decimalSymbol) === 2)
      this._value += '0';
    if (this._value?.length - this._value?.indexOf(this.decimalSymbol) === 1)
      this._value = this._value.substring(0, this._value.length - 1);
    this.onTouched(this.removeCurrencyFormat(this._value));
    event.target.value = this._value;
  }

  private getValueFromEvent(event: any) {
    let value: string = event.target.value.replace(
      this.reNonCurrencySymbols,
      ''
    );
    // remove decimal symbol if more than one
    if (
      (value &&
        value.length - value.replace(this.reDecimalSymbol, '').length > 1) ||
      value.indexOf(this.decimalSymbol) === 0
    ) {
      const indx = value.lastIndexOf(this.decimalSymbol);
      value = value.substring(0, indx) + value.substring(indx + 1);
    }
    let formatted = this.currencyPipe.transform(
      value,
      this.currency,
      this.display,
      this.digitsInfo
    );
    if (formatted && value)
      formatted += value.endsWith(this.decimalSymbol) ? this.decimalSymbol : '';
    return formatted;
  }

  private removeCurrencyFormat(value: any) {
    if (value === null) return null;
    const _val = value.replace(this.reNonCurrencySymbols, '');
    return parseFloat(_val).toFixed(2).toString();
  }

  onChange = (value: string) => {
    return;
  };
  onTouched = (value: string) => {
    return;
  };

  writeValue(rawCurrency: string): void {
    this._value = this.currencyPipe.transform(
      rawCurrency,
      this.currency,
      this.display,
      this.digitsInfo
    );
    // fill with zero if only one digit after decimal symbol
    if (this._value?.length - this._value?.indexOf(this.decimalSymbol) === 2)
      this._value += '0';
    this._renderer.setAttribute(
      this._elementRef.nativeElement,
      'value',
      this._value || ''
    );
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private reQuote(str: string): string {
    return str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
  }
}
